import { TERMS_PAGE } from 'site-settings/site-navigation';
import {
  PrivacyTitle,
  PrivacyUpdated,
  PrivacySection,
  PrivacyH2,
  PrivacyH3,
  PrivacyP,
  PrivacyStrong,
  PrivacyUl,
  PrivacyLi,
  PrivacyAddress,
  PrivacyLink,
  PrivacyMail
} from './privacy-document.style';

const PrivacyPolicyContent = () => (
  <>
    <PrivacyTitle>OFP Wallet Privacy Policy</PrivacyTitle>
    <PrivacyUpdated>Last updated: September 23rd, 2026</PrivacyUpdated>

    <PrivacySection>
      <PrivacyP>
        This Privacy Policy explains how{' '}
        <PrivacyStrong>Open Forest AG</PrivacyStrong> (“
        <PrivacyStrong>Open Forest AG</PrivacyStrong>”, “
        <PrivacyStrong>OF AG</PrivacyStrong>”, “
        <PrivacyStrong>we</PrivacyStrong>”, “
        <PrivacyStrong>us</PrivacyStrong>” or “
        <PrivacyStrong>our</PrivacyStrong>”) collects, uses, stores, shares and
        otherwise processes personal data in connection with the{' '}
        <PrivacyStrong>OFP Wallet</PrivacyStrong>.
      </PrivacyP>
      <PrivacyP>
        Open Forest AG is a company incorporated under the laws of Switzerland,
        registered under number <PrivacyStrong>CHE-396.866.692</PrivacyStrong>,
        with its registered office at:
      </PrivacyP>
      <PrivacyAddress>
        <PrivacyStrong>Open Forest AG</PrivacyStrong>
        c/o Mercandor AG
        <br />
        Unter Altstadt 28
        <br />
        6300 Zug
        <br />
        Switzerland
      </PrivacyAddress>
      <PrivacyP>
        For the purposes of applicable data protection laws, Open Forest AG is
        the data controller in relation to the personal data described in this
        Privacy Policy unless otherwise stated.
      </PrivacyP>
      <PrivacyP>
        This Privacy Policy should be read together with the{' '}
        <PrivacyLink to={TERMS_PAGE} target="_blank" rel="noopener noreferrer">
          OFP Wallet Terms and Conditions
        </PrivacyLink>
        .
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>1. Scope of this Privacy Policy</PrivacyH2>
    <PrivacySection>
      <PrivacyP>This Privacy Policy applies when you:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>access or use the OFP Wallet;</PrivacyLi>
        <PrivacyLi>
          create or access an account connected with the OFP Wallet;
        </PrivacyLi>
        <PrivacyLi>
          receive, hold, transfer, manage or retire supported digital assets
          through the OFP Wallet;
        </PrivacyLi>
        <PrivacyLi>
          interact with OPN Tokens (“OPN”), Open Carbon Credits (“OCCs”) or
          supported smart contracts;
        </PrivacyLi>
        <PrivacyLi>contact us concerning the OFP Wallet;</PrivacyLi>
        <PrivacyLi>interact with OFP Wallet support services; or</PrivacyLi>
        <PrivacyLi>
          otherwise interact with us in connection with the OFP Wallet.
        </PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        This Privacy Policy applies to information that constitutes personal
        data under applicable data protection law.
      </PrivacyP>
      <PrivacyP>
        Information recorded on a public blockchain may be publicly available
        and may not always constitute personal data in every context. However,
        blockchain information may constitute personal data where it can
        reasonably be associated with an identifiable individual.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>2. Applicable data protection law</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        Open Forest AG is established in Switzerland and processes personal data
        primarily in accordance with the{' '}
        <PrivacyStrong>
          Swiss Federal Act on Data Protection (“FADP”)
        </PrivacyStrong>{' '}
        and its implementing regulations.
      </PrivacyP>
      <PrivacyP>
        Where applicable, we also comply with the{' '}
        <PrivacyStrong>
          EU General Data Protection Regulation (“GDPR”)
        </PrivacyStrong>
        , the UK GDPR and other data protection laws that apply to particular
        Users or processing activities.
      </PrivacyP>
      <PrivacyP>
        The application of a particular data protection law depends on the
        circumstances of the processing and the jurisdiction concerned.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>3. Personal data we may collect</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        The personal data we collect depends on how you interact with the OFP
        Wallet and which functionality you use.
      </PrivacyP>

      <PrivacyH3>3.1 Account and identification information</PrivacyH3>
      <PrivacyP>Where applicable, we may collect:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>name;</PrivacyLi>
        <PrivacyLi>email address;</PrivacyLi>
        <PrivacyLi>company or organization name;</PrivacyLi>
        <PrivacyLi>role or affiliation;</PrivacyLi>
        <PrivacyLi>account identifiers;</PrivacyLi>
        <PrivacyLi>username or similar identifiers;</PrivacyLi>
        <PrivacyLi>country or jurisdiction;</PrivacyLi>
        <PrivacyLi>communication preferences; and</PrivacyLi>
        <PrivacyLi>
          other information you provide when creating or managing an account.
        </PrivacyLi>
      </PrivacyUl>

      <PrivacyH3>3.2 Wallet and blockchain information</PrivacyH3>
      <PrivacyP>
        When you use the OFP Wallet, we may process information such as:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>public wallet address;</PrivacyLi>
        <PrivacyLi>blockchain account identifier;</PrivacyLi>
        <PrivacyLi>transaction hashes;</PrivacyLi>
        <PrivacyLi>transaction history;</PrivacyLi>
        <PrivacyLi>OPN balances and transactions;</PrivacyLi>
        <PrivacyLi>OCC balances and transactions;</PrivacyLi>
        <PrivacyLi>
          OCC issuance, transfer or retirement information;
        </PrivacyLi>
        <PrivacyLi>timestamps;</PrivacyLi>
        <PrivacyLi>smart-contract interactions; and</PrivacyLi>
        <PrivacyLi>blockchain network information.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Some or all of this information may also be publicly available directly
        on the relevant blockchain.
      </PrivacyP>

      <PrivacyH3>3.3 Private keys and seed phrases</PrivacyH3>
      <PrivacyP>
        The OFP Wallet is intended to operate on a non-custodial basis unless
        expressly stated otherwise.
      </PrivacyP>
      <PrivacyP>
        <PrivacyStrong>
          Open Forest AG does not collect or store your private key or seed
          phrase as part of the ordinary operation of the OFP Wallet.
        </PrivacyStrong>
      </PrivacyP>
      <PrivacyP>
        You are responsible for safeguarding your private keys, seed phrases and
        other credentials used to control your Wallet.
      </PrivacyP>
      <PrivacyP>
        You should never send your private key or seed phrase to Open Forest AG
        or to any person claiming to represent Open Forest AG.
      </PrivacyP>

      <PrivacyH3>3.4 Identity verification and compliance information</PrivacyH3>
      <PrivacyP>
        Where required for legal, regulatory, security or compliance purposes,
        we may collect or receive information necessary to verify your identity
        or assess activity associated with the OFP Wallet.
      </PrivacyP>
      <PrivacyP>Depending on the circumstances, this may include:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>full legal name;</PrivacyLi>
        <PrivacyLi>date of birth;</PrivacyLi>
        <PrivacyLi>nationality;</PrivacyLi>
        <PrivacyLi>country of residence;</PrivacyLi>
        <PrivacyLi>company information;</PrivacyLi>
        <PrivacyLi>identification documents;</PrivacyLi>
        <PrivacyLi>
          information concerning beneficial ownership or authorized
          representatives;
        </PrivacyLi>
        <PrivacyLi>
          information concerning the source or destination of transactions; and
        </PrivacyLi>
        <PrivacyLi>
          information required for sanctions, anti-money laundering or other
          compliance checks.
        </PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        We only collect such information where it is required or appropriate for
        the relevant service, relationship or legal obligation.
      </PrivacyP>

      <PrivacyH3>3.5 Device and technical information</PrivacyH3>
      <PrivacyP>
        When you access the OFP Wallet, we may automatically collect technical
        information including:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>IP address;</PrivacyLi>
        <PrivacyLi>browser type and version;</PrivacyLi>
        <PrivacyLi>operating system;</PrivacyLi>
        <PrivacyLi>device type;</PrivacyLi>
        <PrivacyLi>language settings;</PrivacyLi>
        <PrivacyLi>date and time of access;</PrivacyLi>
        <PrivacyLi>Wallet application activity;</PrivacyLi>
        <PrivacyLi>error and diagnostic information;</PrivacyLi>
        <PrivacyLi>security events; and</PrivacyLi>
        <PrivacyLi>technical logs.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        We use this information primarily to operate, secure, monitor and
        improve the OFP Wallet.
      </PrivacyP>

      <PrivacyH3>3.6 Communications and support information</PrivacyH3>
      <PrivacyP>If you contact us, we may collect:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>your name;</PrivacyLi>
        <PrivacyLi>email address;</PrivacyLi>
        <PrivacyLi>Wallet address where relevant;</PrivacyLi>
        <PrivacyLi>correspondence;</PrivacyLi>
        <PrivacyLi>support requests;</PrivacyLi>
        <PrivacyLi>
          information concerning problems or transactions you report; and
        </PrivacyLi>
        <PrivacyLi>any other information you choose to provide.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Please do not include private keys or seed phrases in communications
        with us.
      </PrivacyP>

      <PrivacyH3>3.7 Cookies and similar technologies</PrivacyH3>
      <PrivacyP>
        Our websites and web-based Wallet interfaces may use cookies and similar
        technologies necessary to:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>operate the service;</PrivacyLi>
        <PrivacyLi>maintain sessions;</PrivacyLi>
        <PrivacyLi>provide security functionality;</PrivacyLi>
        <PrivacyLi>remember preferences;</PrivacyLi>
        <PrivacyLi>understand technical performance; and</PrivacyLi>
        <PrivacyLi>analyze use of our services.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Where required by applicable law, non-essential cookies or similar
        technologies will only be used with your consent.
      </PrivacyP>
      <PrivacyP>
        Further information may be provided through our Cookie Policy or cookie
        settings.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>4. How we collect personal data</PrivacyH2>
    <PrivacySection>
      <PrivacyP>We may obtain personal data:</PrivacyP>

      <PrivacyH3>4.1 Directly from you</PrivacyH3>
      <PrivacyP>For example when you:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>create an account;</PrivacyLi>
        <PrivacyLi>contact us;</PrivacyLi>
        <PrivacyLi>
          provide information for verification or compliance purposes;
        </PrivacyLi>
        <PrivacyLi>submit a support request; or</PrivacyLi>
        <PrivacyLi>otherwise communicate with us.</PrivacyLi>
      </PrivacyUl>

      <PrivacyH3>4.2 Automatically through your use of the OFP Wallet</PrivacyH3>
      <PrivacyP>For example through:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>system logs;</PrivacyLi>
        <PrivacyLi>authentication systems;</PrivacyLi>
        <PrivacyLi>device information;</PrivacyLi>
        <PrivacyLi>security monitoring;</PrivacyLi>
        <PrivacyLi>cookies; and</PrivacyLi>
        <PrivacyLi>technical analytics.</PrivacyLi>
      </PrivacyUl>

      <PrivacyH3>4.3 From public blockchains</PrivacyH3>
      <PrivacyP>
        Because the OFP Wallet interacts with blockchain infrastructure, we may
        obtain or analyze information publicly recorded on a blockchain,
        including wallet addresses, transactions and smart-contract
        interactions.
      </PrivacyP>

      <PrivacyH3>4.4 From third parties</PrivacyH3>
      <PrivacyP>Where necessary, we may receive information from:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>authentication providers;</PrivacyLi>
        <PrivacyLi>technical infrastructure providers;</PrivacyLi>
        <PrivacyLi>blockchain infrastructure providers;</PrivacyLi>
        <PrivacyLi>compliance or identity-verification providers;</PrivacyLi>
        <PrivacyLi>project participants;</PrivacyLi>
        <PrivacyLi>business partners;</PrivacyLi>
        <PrivacyLi>public registers; or</PrivacyLi>
        <PrivacyLi>competent public authorities.</PrivacyLi>
      </PrivacyUl>
    </PrivacySection>

    <PrivacyH2>5. Why we process personal data</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        We process personal data where necessary for the operation and
        protection of the OFP Wallet and Open Forest Protocol ecosystem.
      </PrivacyP>
      <PrivacyP>Our purposes may include:</PrivacyP>

      <PrivacyH3>5.1 Providing the OFP Wallet</PrivacyH3>
      <PrivacyP>We process information to:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>provide access to the OFP Wallet;</PrivacyLi>
        <PrivacyLi>authenticate Users;</PrivacyLi>
        <PrivacyLi>display Wallet information;</PrivacyLi>
        <PrivacyLi>
          facilitate interaction with supported blockchain infrastructure;
        </PrivacyLi>
        <PrivacyLi>display OPN and OCC information;</PrivacyLi>
        <PrivacyLi>facilitate supported Wallet functionality; and</PrivacyLi>
        <PrivacyLi>maintain User accounts.</PrivacyLi>
      </PrivacyUl>

      <PrivacyH3>5.2 Operating OPN and OCC functionality</PrivacyH3>
      <PrivacyP>We may process information in order to:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>
          associate Wallet accounts with relevant Open Forest Protocol activity;
        </PrivacyLi>
        <PrivacyLi>display OPN or OCC balances;</PrivacyLi>
        <PrivacyLi>facilitate transfers;</PrivacyLi>
        <PrivacyLi>record or display OCC issuance;</PrivacyLi>
        <PrivacyLi>support OCC retirement;</PrivacyLi>
        <PrivacyLi>interact with applicable smart contracts; and</PrivacyLi>
        <PrivacyLi>
          maintain records necessary for the operation and integrity of the Open
          Forest Protocol.
        </PrivacyLi>
      </PrivacyUl>

      <PrivacyH3>5.3 Security and fraud prevention</PrivacyH3>
      <PrivacyP>We process information to:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>protect the OFP Wallet;</PrivacyLi>
        <PrivacyLi>detect unauthorized access;</PrivacyLi>
        <PrivacyLi>investigate suspicious activity;</PrivacyLi>
        <PrivacyLi>prevent fraud;</PrivacyLi>
        <PrivacyLi>identify technical vulnerabilities;</PrivacyLi>
        <PrivacyLi>maintain system integrity; and</PrivacyLi>
        <PrivacyLi>respond to security incidents.</PrivacyLi>
      </PrivacyUl>

      <PrivacyH3>5.4 Legal and regulatory compliance</PrivacyH3>
      <PrivacyP>
        We may process personal data where necessary to:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>comply with applicable laws;</PrivacyLi>
        <PrivacyLi>comply with sanctions requirements;</PrivacyLi>
        <PrivacyLi>
          carry out legally required identification or verification;
        </PrivacyLi>
        <PrivacyLi>
          respond to lawful requests from competent authorities;
        </PrivacyLi>
        <PrivacyLi>
          establish, exercise or defend legal claims; and
        </PrivacyLi>
        <PrivacyLi>
          comply with accounting, tax or record-keeping obligations.
        </PrivacyLi>
      </PrivacyUl>

      <PrivacyH3>5.5 Support and communications</PrivacyH3>
      <PrivacyP>We process information to:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>respond to questions;</PrivacyLi>
        <PrivacyLi>
          investigate reported transactions or technical issues;
        </PrivacyLi>
        <PrivacyLi>provide technical support;</PrivacyLi>
        <PrivacyLi>communicate changes affecting the OFP Wallet;</PrivacyLi>
        <PrivacyLi>provide security notices; and</PrivacyLi>
        <PrivacyLi>
          communicate changes to our Terms or this Privacy Policy.
        </PrivacyLi>
      </PrivacyUl>

      <PrivacyH3>5.6 Improving the OFP Wallet</PrivacyH3>
      <PrivacyP>
        We may use technical and usage information to:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>understand how the OFP Wallet is used;</PrivacyLi>
        <PrivacyLi>identify errors and performance issues;</PrivacyLi>
        <PrivacyLi>improve usability;</PrivacyLi>
        <PrivacyLi>improve security;</PrivacyLi>
        <PrivacyLi>develop new features; and</PrivacyLi>
        <PrivacyLi>
          maintain and improve Open Forest Protocol infrastructure.
        </PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Where possible, we use aggregated or anonymized information for these
        purposes.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>6. Legal bases for processing</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        Where the GDPR or another law requiring a specific legal basis applies,
        we process personal data on one or more of the following bases:
      </PrivacyP>

      <PrivacyH3>Performance of a contract</PrivacyH3>
      <PrivacyP>
        Processing may be necessary to provide the OFP Wallet and perform our
        obligations under the OFP Wallet Terms of Use.
      </PrivacyP>

      <PrivacyH3>Legitimate interests</PrivacyH3>
      <PrivacyP>
        We may process personal data where necessary for legitimate interests
        including:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>operating and improving the OFP Wallet;</PrivacyLi>
        <PrivacyLi>maintaining the security of our systems;</PrivacyLi>
        <PrivacyLi>preventing fraud and misuse;</PrivacyLi>
        <PrivacyLi>supporting Users;</PrivacyLi>
        <PrivacyLi>
          protecting Open Forest AG and the Open Forest Protocol ecosystem; and
        </PrivacyLi>
        <PrivacyLi>
          maintaining appropriate business and technical records.
        </PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        We consider the impact of such processing on affected individuals when
        relying on legitimate interests.
      </PrivacyP>

      <PrivacyH3>Legal obligations</PrivacyH3>
      <PrivacyP>
        We may process information where necessary to comply with legal or
        regulatory obligations.
      </PrivacyP>

      <PrivacyH3>Consent</PrivacyH3>
      <PrivacyP>
        Where required, we may process information based on your consent, for
        example for certain cookies, analytics or communications.
      </PrivacyP>
      <PrivacyP>
        Where processing is based on consent, you may withdraw that consent at
        any time, without affecting processing that occurred before withdrawal.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>7. Public blockchain information</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        Blockchain technology has characteristics that differ from conventional
        centralized databases.
      </PrivacyP>
      <PrivacyP>
        Information submitted to a public blockchain may:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>be publicly visible;</PrivacyLi>
        <PrivacyLi>be accessible worldwide;</PrivacyLi>
        <PrivacyLi>be copied or analyzed by third parties;</PrivacyLi>
        <PrivacyLi>remain available indefinitely; and</PrivacyLi>
        <PrivacyLi>
          be technically impossible for Open Forest AG to alter or delete.
        </PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Open Forest AG does not control the underlying public blockchain and
        therefore cannot generally erase, amend or reverse information that has
        already been validly recorded on it.
      </PrivacyP>
      <PrivacyP>
        If you exercise a data protection right concerning information recorded
        on a blockchain, we will assess the request in accordance with
        applicable law and the technical possibilities available to us.
      </PrivacyP>
      <PrivacyP>
        Where appropriate, measures may instead be applied to information stored
        in systems controlled by Open Forest AG.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>8. Automated processing and blockchain analysis</PrivacyH2>
    <PrivacySection>
      <PrivacyP>We may use automated technical tools to:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>monitor Wallet security;</PrivacyLi>
        <PrivacyLi>
          detect unusual or potentially fraudulent activity;
        </PrivacyLi>
        <PrivacyLi>identify technical errors;</PrivacyLi>
        <PrivacyLi>analyze blockchain transactions;</PrivacyLi>
        <PrivacyLi>
          perform sanctions or compliance screening where required; and
        </PrivacyLi>
        <PrivacyLi>protect the Open Forest Protocol ecosystem.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        These tools may generate alerts or risk indicators.
      </PrivacyP>
      <PrivacyP>
        Where applicable law gives you rights concerning a decision based solely
        on automated processing that produces legal or similarly significant
        effects, we will respect those rights.
      </PrivacyP>
      <PrivacyP>
        We do not intend to make significant decisions about Users solely
        through automated processing unless this is permitted by applicable law
        and appropriate safeguards are in place.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>9. How we share personal data</PrivacyH2>
    <PrivacySection>
      <PrivacyP>We do not sell personal data.</PrivacyP>
      <PrivacyP>
        We may disclose personal data where necessary to the following
        categories of recipients.
      </PrivacyP>

      <PrivacyH3>9.1 Technical service providers</PrivacyH3>
      <PrivacyP>These may include providers of:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>hosting and cloud infrastructure;</PrivacyLi>
        <PrivacyLi>authentication services;</PrivacyLi>
        <PrivacyLi>blockchain infrastructure;</PrivacyLi>
        <PrivacyLi>node services;</PrivacyLi>
        <PrivacyLi>cybersecurity services;</PrivacyLi>
        <PrivacyLi>analytics;</PrivacyLi>
        <PrivacyLi>technical monitoring;</PrivacyLi>
        <PrivacyLi>communication services; and</PrivacyLi>
        <PrivacyLi>customer support systems.</PrivacyLi>
      </PrivacyUl>

      <PrivacyH3>9.2 Compliance and professional service providers</PrivacyH3>
      <PrivacyP>We may share information with:</PrivacyP>
      <PrivacyUl>
        <PrivacyLi>identity-verification providers;</PrivacyLi>
        <PrivacyLi>compliance and sanctions-screening providers;</PrivacyLi>
        <PrivacyLi>lawyers;</PrivacyLi>
        <PrivacyLi>accountants;</PrivacyLi>
        <PrivacyLi>auditors;</PrivacyLi>
        <PrivacyLi>insurers; and</PrivacyLi>
        <PrivacyLi>other professional advisers.</PrivacyLi>
      </PrivacyUl>

      <PrivacyH3>9.3 Open Forest Protocol entities and partners</PrivacyH3>
      <PrivacyP>
        Where necessary for the operation of the Open Forest Protocol ecosystem,
        information may be shared with entities cooperating with Open Forest AG,
        including <PrivacyStrong>Open Planet Foundation</PrivacyStrong>, provided
        that there is an appropriate purpose and legal basis for such sharing.
      </PrivacyP>
      <PrivacyP>
        For example, this may be necessary where Wallet information needs to be
        associated with participation in the validator network, project
        activities, OCC issuance or another Open Forest Protocol service.
      </PrivacyP>

      <PrivacyH3>9.4 Public authorities</PrivacyH3>
      <PrivacyP>
        We may disclose information where required by law or where necessary to
        respond to a valid request from a court, regulator, law-enforcement
        agency or other competent authority.
      </PrivacyP>

      <PrivacyH3>9.5 Corporate transactions</PrivacyH3>
      <PrivacyP>
        If Open Forest AG is involved in a merger, restructuring, financing,
        acquisition or transfer of relevant business assets, personal data may
        be disclosed to relevant parties subject to appropriate safeguards.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>10. International data transfers</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        The OFP Wallet is accessible internationally and some of our service
        providers may process data outside Switzerland.
      </PrivacyP>
      <PrivacyP>
        Where personal data is transferred internationally, Open Forest AG takes
        appropriate measures required by applicable data protection law.
      </PrivacyP>
      <PrivacyP>
        Depending on the destination and applicable law, these measures may
        include:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>
          transferring data to jurisdictions recognized as providing an adequate
          level of protection;
        </PrivacyLi>
        <PrivacyLi>contractual safeguards;</PrivacyLi>
        <PrivacyLi>standard contractual clauses;</PrivacyLi>
        <PrivacyLi>
          additional technical or organizational measures; or
        </PrivacyLi>
        <PrivacyLi>another legally permitted transfer mechanism.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Public blockchain information is inherently distributed and may be
        accessed by blockchain participants worldwide. The geographical location
        of all nodes processing public blockchain data cannot generally be
        controlled by Open Forest AG.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>11. Data retention</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        We retain personal data only for as long as reasonably necessary for the
        purposes for which it was collected, taking into account:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>the nature of the information;</PrivacyLi>
        <PrivacyLi>the purpose of the processing;</PrivacyLi>
        <PrivacyLi>security requirements;</PrivacyLi>
        <PrivacyLi>our relationship with the User;</PrivacyLi>
        <PrivacyLi>applicable limitation periods; and</PrivacyLi>
        <PrivacyLi>legal, accounting or regulatory requirements.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Certain corporate, accounting and business records may need to be
        retained for <PrivacyStrong>up to ten years</PrivacyStrong> under
        applicable Swiss law.
      </PrivacyP>
      <PrivacyP>
        Technical and security logs may be retained for periods appropriate to
        security, troubleshooting and fraud-prevention requirements.
      </PrivacyP>
      <PrivacyP>
        Where information is no longer required, it will be deleted, anonymized
        or otherwise handled in accordance with applicable law.
      </PrivacyP>

      <PrivacyH3>Blockchain data</PrivacyH3>
      <PrivacyP>
        Information recorded directly on a public blockchain may remain
        permanently available due to the technical characteristics of blockchain
        technology.
      </PrivacyP>
      <PrivacyP>
        Open Forest AG generally cannot delete data from an independent public
        blockchain.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>12. Data security</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        We implement reasonable technical and organizational measures designed
        to protect personal data against:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>unauthorized access;</PrivacyLi>
        <PrivacyLi>unauthorized disclosure;</PrivacyLi>
        <PrivacyLi>alteration;</PrivacyLi>
        <PrivacyLi>loss;</PrivacyLi>
        <PrivacyLi>misuse; and</PrivacyLi>
        <PrivacyLi>destruction.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Measures may include access controls, security monitoring,
        authentication mechanisms, encryption where appropriate, operational
        procedures and restrictions on employee and service-provider access.
      </PrivacyP>
      <PrivacyP>
        However, no internet-connected or blockchain-based system can be
        guaranteed to be completely secure.
      </PrivacyP>
      <PrivacyP>
        Users are responsible for maintaining the security of their own devices,
        accounts, private keys and authentication credentials.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>13. Data breaches and security incidents</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        If Open Forest AG becomes aware of a personal-data security breach, we
        will investigate the incident and take appropriate measures.
      </PrivacyP>
      <PrivacyP>
        Where required by applicable law, we will notify the relevant data
        protection authority and/or affected individuals.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>14. Your data protection rights</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        Your rights depend on the data protection laws applicable to you.
      </PrivacyP>
      <PrivacyP>
        Subject to applicable limitations and exceptions, you may have the right
        to:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>
          obtain information about whether we process personal data concerning
          you;
        </PrivacyLi>
        <PrivacyLi>request access to your personal data;</PrivacyLi>
        <PrivacyLi>request correction of inaccurate personal data;</PrivacyLi>
        <PrivacyLi>request deletion of personal data;</PrivacyLi>
        <PrivacyLi>request restriction of processing;</PrivacyLi>
        <PrivacyLi>object to certain processing;</PrivacyLi>
        <PrivacyLi>
          receive certain personal data in a portable format;
        </PrivacyLi>
        <PrivacyLi>
          withdraw consent where processing is based on consent; and
        </PrivacyLi>
        <PrivacyLi>
          lodge a complaint with a competent data protection authority.
        </PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Under Swiss data protection law, individuals may in particular request
        information concerning the personal data being processed about them and
        may request correction or, where applicable, deletion or restriction.
      </PrivacyP>
      <PrivacyP>
        Some rights cannot be fully exercised in relation to information already
        recorded on an independent public blockchain because Open Forest AG does
        not have technical control over that data.
      </PrivacyP>
      <PrivacyP>
        This does not affect your rights concerning personal data stored in
        systems controlled by Open Forest AG.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>15. How to exercise your rights</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        To exercise a data protection right or ask a question about this Privacy
        Policy, please contact:
      </PrivacyP>
      <PrivacyAddress>
        <PrivacyStrong>Open Forest AG</PrivacyStrong>
        c/o Mercandor AG
        <br />
        Unter Altstadt 28
        <br />
        6300 Zug
        <br />
        Switzerland
      </PrivacyAddress>
      <PrivacyP>
        <PrivacyStrong>Email:</PrivacyStrong>{' '}
        <PrivacyMail href="mailto:support@openforestprotocol.org">
          support@openforestprotocol.org
        </PrivacyMail>
      </PrivacyP>
      <PrivacyP>
        We may need to verify your identity before responding to a request.
      </PrivacyP>
      <PrivacyP>
        We will respond within the period required by applicable law.
      </PrivacyP>
      <PrivacyP>
        Under Swiss law, requests for access are generally answered within 30
        days. Where the GDPR applies, requests are generally handled within one
        month, subject to legally permitted extensions.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>16. Children</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        The OFP Wallet is not intended for use by children who are not legally
        capable of entering into the applicable agreement.
      </PrivacyP>
      <PrivacyP>
        We do not knowingly collect personal data from children in circumstances
        where such processing would be unlawful.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>17. Third-party services</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        The OFP Wallet may contain links to or interact with services operated
        by third parties.
      </PrivacyP>
      <PrivacyP>
        Those third parties may independently collect or process personal data
        and may act as separate data controllers.
      </PrivacyP>
      <PrivacyP>
        Their processing is governed by their own privacy notices and not by
        this Privacy Policy.
      </PrivacyP>
      <PrivacyP>
        Users should review the relevant privacy information before using
        independent third-party services.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>18. Changes to this Privacy Policy</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        We may update this Privacy Policy from time to time to reflect:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>changes to the OFP Wallet;</PrivacyLi>
        <PrivacyLi>changes to OPN or OCC functionality;</PrivacyLi>
        <PrivacyLi>new service providers;</PrivacyLi>
        <PrivacyLi>technological developments;</PrivacyLi>
        <PrivacyLi>changes to our processing activities; or</PrivacyLi>
        <PrivacyLi>changes in applicable law.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        The latest version will be made available through the OFP Wallet or Open
        Forest Protocol website.
      </PrivacyP>
      <PrivacyP>
        Where changes materially affect how we process personal data, we will
        take reasonable steps to inform affected Users.
      </PrivacyP>
    </PrivacySection>

    <PrivacyH2>19. Contact</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        Questions regarding this Privacy Policy or Open Forest AG’s processing of
        personal data may be addressed to:
      </PrivacyP>
      <PrivacyAddress>
        <PrivacyStrong>Open Forest AG</PrivacyStrong>
        c/o Mercandor AG
        <br />
        Unter Altstadt 28
        <br />
        6300 Zug
        <br />
        Switzerland
      </PrivacyAddress>
      <PrivacyP>
        <PrivacyStrong>Email:</PrivacyStrong>{' '}
        <PrivacyMail href="mailto:support@openforestprotocol.org">
          support@openforestprotocol.org
        </PrivacyMail>
      </PrivacyP>
    </PrivacySection>
  </>
);

export default PrivacyPolicyContent;
